import { EMPTY_ARRAY, IconName, logger, useWLOrgConfigContext, useWLRoleAuth, type WLOrgConfig } from '@talos/kyoko';
import { compact, concat, uniqBy } from 'lodash-es';
import { useMemo } from 'react';
import {
  AddressesScreen,
  FeeTiersScreen,
  SettingsScreen,
  WatchListScreen,
} from '../../layouts/mobileScreens/screenLibrary';
import type { LayoutComponent } from '../../layouts/types';
import { checkRequiredPermissions } from '../../utils/layoutPermissions';

export function usePermissionedMobileScreens(layoutComponent: LayoutComponent): {
  screens: NonNullable<LayoutComponent['mobileScreens']>;
} {
  const { isAuthorized } = useWLRoleAuth();
  const { config } = useWLOrgConfigContext();

  return useMemo(
    () => getPermissionedMobileScreens(layoutComponent.mobileScreens, config, isAuthorized),
    [config, isAuthorized, layoutComponent.mobileScreens]
  );
}

export const getPermissionedMobileScreens = (
  mobileScreens: LayoutComponent['mobileScreens'] = EMPTY_ARRAY,
  config: WLOrgConfig,
  isAuthorized: (action: string) => boolean
): { screens: NonNullable<LayoutComponent['mobileScreens']> } => {
  const screens = [...mobileScreens];

  // Add screens from config
  if (config.navigationItems) {
    for (const navItem of config.navigationItems) {
      let handled = false;
      switch (navItem.path) {
        case '/':
          screens.push({
            ...WatchListScreen,
            id: '/',
            requiredPermissions: compact(concat(WatchListScreen.requiredPermissions, navItem.requiredPermissions)),
            requiredConfigs: compact(concat(WatchListScreen.requiredConfigs, navItem.requiredConfigs)),
          });
          handled = true;
          break;
        case '/settings':
          screens.push({
            ...SettingsScreen,
            requiredPermissions: compact(concat(SettingsScreen.requiredPermissions, navItem.requiredPermissions)),
            requiredConfigs: compact(concat(SettingsScreen.requiredConfigs, navItem.requiredConfigs)),
          });
          handled = true;
          break;
        case '/addresses':
          screens.push({
            ...AddressesScreen,
            requiredPermissions: compact(concat(AddressesScreen.requiredPermissions, navItem.requiredPermissions)),
            requiredConfigs: compact(concat(AddressesScreen.requiredConfigs, navItem.requiredConfigs)),
          });
          handled = true;
          break;
        case '/fee-tiers':
          screens.push({
            ...FeeTiersScreen,
            requiredPermissions: compact(concat(FeeTiersScreen.requiredPermissions, navItem.requiredPermissions)),
            requiredConfigs: compact(concat(FeeTiersScreen.requiredConfigs, navItem.requiredConfigs)),
          });
          handled = true;
          break;
      }
      const isExternal = navItem.path.startsWith('//') || navItem.path.startsWith('http');
      if (isExternal) {
        handled = true;
        screens.push({
          ...navItem,
          icon: navItem.icon || IconName.ExternalLink,
          component: null,
          id: `external-link-${navItem.path}`,
        });
      }
      if (!handled) {
        logger.error(new Error(`Unhandled navigationItems: ${navItem.label} with path: ${navItem.path}`), {
          extra: { navItem },
        });
      }
    }
  }

  return {
    screens: uniqBy(
      screens.filter(screen => {
        return (
          checkRequiredPermissions(screen.requiredPermissions, isAuthorized) &&
          (screen.requiredConfigs == null || screen.requiredConfigs?.every(prop => config[prop]))
        );
      }),
      'id'
    ),
  };
};
